import * as XLSX from "xlsx";

const CORRECT_ANSWER_ALIASES = {
  option_1: "option_1",
  option1: "option_1",
  "option 1": "option_1",
  "1": "option_1",
  a: "option_1",
  option_2: "option_2",
  option2: "option_2",
  "option 2": "option_2",
  "2": "option_2",
  b: "option_2",
  option_3: "option_3",
  option3: "option_3",
  "option 3": "option_3",
  "3": "option_3",
  c: "option_3",
  option_4: "option_4",
  option4: "option_4",
  "option 4": "option_4",
  "4": "option_4",
  d: "option_4",
};

const HEADER_ALIASES = {
  question: "question",
  "question text": "question",
  questions: "question",
  option_1: "option_1",
  option1: "option_1",
  "option 1": "option_1",
  option_2: "option_2",
  option2: "option_2",
  "option 2": "option_2",
  option_3: "option_3",
  option3: "option_3",
  "option 3": "option_3",
  option_4: "option_4",
  option4: "option_4",
  "option 4": "option_4",
  correct_answer: "correct_answer",
  "correct answer": "correct_answer",
  answer: "correct_answer",
  "correct option": "correct_answer",
  marks: "marks",
  mark: "marks",
  "question marks": "marks",
  score: "marks",
  points: "marks",
};

const emptyQuestionRow = () => ({
  question: "",
  option_1: "",
  option_2: "",
  option_3: "",
  option_4: "",
  correct_answer: "",
  marks: "1",
});

export function normalizeModuleQuestionCorrectAnswer(value, row = {}) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const mapped = CORRECT_ANSWER_ALIASES[raw.toLowerCase()];
  if (mapped) return mapped;

  const optionKeys = ["option_1", "option_2", "option_3", "option_4"];
  const match = optionKeys.find(
    (key) => String(row[key] || "").trim().toLowerCase() === raw.toLowerCase()
  );
  return match || "";
}

function normalizeHeader(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function mapRow(raw) {
  const row = emptyQuestionRow();
  Object.entries(raw || {}).forEach(([header, value]) => {
    const key = HEADER_ALIASES[normalizeHeader(header)];
    if (!key) return;
    row[key] = value == null ? "" : String(value).trim();
  });
  row.correct_answer = normalizeModuleQuestionCorrectAnswer(
    row.correct_answer,
    row
  );
  const marksNumber = Number(row.marks);
  row.marks =
    Number.isFinite(marksNumber) && marksNumber > 0
      ? String(Math.round(marksNumber * 100) / 100)
      : "1";
  return row;
}

export function isFilledModuleQuestionRow(row) {
  if (!row || typeof row !== "object") return false;
  return Boolean(String(row.question || "").trim());
}

export async function parseModuleQuestionsFile(file) {
  if (!file) {
    return { rows: [], error: "Choose an Excel or CSV file." };
  }

  const name = String(file.name || "").toLowerCase();
  const isSpreadsheet = /\.(xlsx|xls|csv)$/i.test(name);
  if (!isSpreadsheet) {
    return { rows: [], error: "Use an .xlsx, .xls, or .csv file." };
  }

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    return { rows: [], error: "The file has no sheets." };
  }

  const sheet = workbook.Sheets[sheetName];
  const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });
  const rows = json.map(mapRow).filter(isFilledModuleQuestionRow);

  if (!rows.length) {
    return {
      rows: [],
      error:
        "No questions found. Use columns: Question, Option 1, Option 2, Option 3, Option 4, Correct Answer, Marks.",
    };
  }

  return { rows, error: null };
}

export function downloadModuleQuestionsTemplate() {
  const headers = [
    "Question",
    "Option 1",
    "Option 2",
    "Option 3",
    "Option 4",
    "Correct Answer",
    "Marks",
  ];
  const sample = [
    "What is 2 + 2?",
    "3",
    "4",
    "5",
    "22",
    "Option 2",
    "1",
  ];
  const worksheet = XLSX.utils.aoa_to_sheet([headers, sample]);
  worksheet["!cols"] = headers.map(() => ({ wch: 24 }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Questions");
  XLSX.writeFile(workbook, "module_questions_template.xlsx");
}

export { emptyQuestionRow };
